import {
  PlanningEvent,
  PlanningStatus,
  PlanningSeverity,
} from "../models/planning-event.model";
import { PdcStatus } from "../../of/enums/pdc-status.enum";

/**
 * Mapper PDC → PlanningEvent. Génère des events dédiés au cycle PDC :
 *
 * - `pdcStatus ∈ {A_PREPARER, EN_COURS_PREPA}` → event "Préparer PDC" ancré sur
 *   `dateDebut - preparationOffsetDays` (par défaut J-1)
 * - `pdcStatus = A_REINTEGRER` → event "Réintégrer PDC" ancré sur `dateFin`
 *   ou `new Date()` si pas de dateFin
 *
 * Les OFs `PRET`, `EN_CONSOMMATION`, `REINTEGRE`, `null` ne génèrent aucun event
 * PDC (pas d'action requise du logisticien pour la V1).
 *
 * @param ofs — liste d'OFs (le champ `pdcStatus` doit être chargé)
 * @param creneaux — optionnel : créneaux de planification (pour `datePlanif`)
 * @param preparationOffsetDays — nb de jours avant `dateDebut` pour l'event
 *   "Préparer PDC". Défaut 1 (= veille du lancement).
 */
export function pdcToPlanningEvents(
  ofs: any[],
  creneaux?: any[],
  preparationOffsetDays = 1,
): PlanningEvent[] {
  if (!ofs?.length) return [];

  const today = new Date();

  // Index créneaux par ofId (plus proche d'aujourd'hui)
  const creneauByOfId = new Map<string, any>();
  if (creneaux?.length) {
    for (const c of creneaux) {
      if (c.ofId && c.type === "PLANIFICATION") {
        const existing = creneauByOfId.get(c.ofId);
        if (
          !existing ||
          Math.abs(new Date(c.start).getTime() - today.getTime()) <
            Math.abs(new Date(existing.start).getTime() - today.getTime())
        ) {
          creneauByOfId.set(c.ofId, c);
        }
      }
    }
  }

  const events: PlanningEvent[] = [];

  for (const o of ofs) {
    const pdc: PdcStatus | null = o.pdcStatus ?? null;
    if (!pdc) continue;

    // L'API `/of/navfilter` renvoie `articleInfos.denomination` (pas `article`
    // eager). On priorise articleInfos, fallback article (si OF chargé eager).
    const article =
      o.articleInfos?.denomination ?? o.article?.denomination ?? "";
    const ligne = o.ligne?.denomination ?? "";
    const numero = o.numero ?? "?";
    const baseLabel = article || numero;

    // Event "Préparer PDC" ancré sur dateDebut - preparationOffsetDays
    if (pdc === PdcStatus.A_PREPARER || pdc === PdcStatus.EN_COURS_PREPA) {
      const creneau = creneauByOfId.get(o.id);
      const dateStart = creneau?.start ?? o.dateDebut ?? null;
      if (!dateStart) continue;

      const prepaDate = new Date(dateStart);
      prepaDate.setDate(prepaDate.getDate() - preparationOffsetDays);

      const { status, severity } = mapPdcToPlanningStatus(pdc, prepaDate, today);

      events.push({
        id: `pdc-prepa:${o.id}`,
        source: "of" as const,
        kind: "pdc-prepa",
        reference: {
          label: baseLabel,
          route: ["/productions/suivi/ope"],
          queryParams: { ofId: o.id },
        },
        datePrevu: prepaDate.toISOString(),
        dateReel: null,
        status,
        severity,
        quantite: undefined,
        payload: {
          article,
          ligne,
          numero,
          etat: pdc,
          etatLabel:
            pdc === PdcStatus.A_PREPARER ? "Préparer PDC" : "Prépa en cours",
          prepaPdc: true,
          /** Date prévue de démarrage de l'OF (pour tooltip). */
          ofDateDebut: new Date(dateStart).toISOString(),
        },
      } satisfies PlanningEvent);
    }

    // Event "Réintégrer PDC" ancré sur dateFin (ou today si pas de dateFin)
    if (pdc === PdcStatus.A_REINTEGRER) {
      const reinteDate = o.dateFin ? new Date(o.dateFin) : new Date();

      events.push({
        id: `pdc-reinte:${o.id}`,
        source: "of" as const,
        kind: "pdc-reinte",
        reference: {
          label: baseLabel,
          route: ["/productions/suivi/ope"],
          queryParams: { ofId: o.id },
        },
        datePrevu: reinteDate.toISOString(),
        dateReel: null,
        status: "late" as PlanningStatus,
        severity: 2 as PlanningSeverity,
        quantite: undefined,
        payload: {
          article,
          ligne,
          numero,
          etat: pdc,
          etatLabel: "Réintégrer PDC",
          reintePdc: true,
        },
      } satisfies PlanningEvent);
    }
  }

  return events;
}

function mapPdcToPlanningStatus(
  pdc: PdcStatus,
  prepaDate: Date,
  today: Date,
): { status: PlanningStatus; severity: PlanningSeverity } {
  // La date de prépa est passée → c'est en retard
  const prepaDay = new Date(prepaDate);
  prepaDay.setHours(23, 59, 59, 999);
  if (prepaDay < today) {
    return { status: "late", severity: 2 };
  }
  if (pdc === PdcStatus.EN_COURS_PREPA) {
    return { status: "in_progress", severity: 2 };
  }
  // A_PREPARER : action requise du logisticien → severity 1 minimum pour
  // remonter dans le tri (les events PDC ne doivent pas être noyés sous
  // la masse des commandes/livraisons normales).
  return { status: "pending", severity: 1 };
}
